import React, { useEffect, useState } from 'react';
import QuestionCard from './QuestionCard';
import { OptionPercentageStat } from './OptionPercentageStat';
import { isOrphanCard, ORPHAN_CARD_CLASSES } from './orphan-card';
import {
  getPickOptionTextClass,
  getPickResultSurfaceClass,
  hasPickResult,
} from './shared/pickResultStyles';
import {
  getPickLockedBackgroundClass,
  getPickLockedBorderClass,
  getPickLockedLabelClass,
} from './shared/pickLockedStyles';
import { LockBadge } from './shared/LockBadge';

export interface CustomOption {
  id: string;
  label: string;
  imageUrl?: string | null;
  popularityPct?: number;
  thirdImageUrl?: string | null;
}

interface CustomPickProps {
  questionId?: string;
  questionText: string;
  subtext?: string;
  /**
   * Optional question-level image rendered above the question text.
   * Used for image-identification questions (e.g. "Who is this coach?",
   * "What brand is this logo?") where the image IS the question prompt
   * and options are text-only to avoid telegraphing the answer.
   * See DKPLAT-363.
   */
  questionImageUrl?: string | null;
  options: CustomOption[];
  selectedOption?: string;
  onOptionSelect: (optionId: string | undefined) => void;
  points: number | string;
  earnedPoints?: number;
  isCorrect?: boolean | null;
  disabled?: boolean;
  locked?: boolean;
  className?: string;
  questionNumber?: number;
  questionMetadata?: {
    dailyDouble?: boolean;
  };
}

const CustomPick: React.FC<CustomPickProps> = ({
  questionId,
  questionText,
  subtext,
  questionImageUrl,
  options,
  selectedOption,
  onOptionSelect,
  points,
  earnedPoints,
  isCorrect,
  disabled = false,
  locked = false,
  className = '',
  questionNumber,
}) => {
  // Daily Double chrome is unused for Circus Daily — ignore metadata styling.
  const isDailyDouble = false;
  const hasResult = hasPickResult(isCorrect);

  // Track image load failures so the whole wrapper collapses on error —
  // hiding only the <img> via style.display would leave the mt/mb margins
  // as a blank gap above the question text. Reset on URL change so a
  // later swap (e.g. admin edit) gets a fresh attempt.
  const [imageErrored, setImageErrored] = useState(false);
  useEffect(() => {
    setImageErrored(false);
  }, [questionImageUrl]);

  return (
    <QuestionCard
      points={points}
      earnedPoints={earnedPoints}
      className={className}
      questionNumber={questionNumber}
      isDailyDouble={isDailyDouble}
      isCorrect={isCorrect}
      data-testid={questionId ? `question-${questionId}` : undefined}
      data-testtype="custom"
    >
      {questionImageUrl && !imageErrored && (
        <div className="flex justify-center mb-3 mt-2">
          <img
            src={questionImageUrl}
            alt=""
            data-testid="custom-question-image"
            className="max-h-32 object-contain"
            onError={() => setImageErrored(true)}
          />
        </div>
      )}

      {subtext && (
        <span
          data-testid="custom-subtext"
          className="text-muted-foreground font-sans font-normal block text-center mt-2 text-sm"
        >
          {subtext}
        </span>
      )}

      <span
        data-testid="custom-question"
        className="text-white font-sans font-normal block text-center mb-4 text-sm leading-5"
      >
        {questionText}
      </span>

      <div
        className={`grid gap-3 ${options.length > 4 ? 'grid-cols-1' : 'grid-cols-2'}`}
        data-testid="custom-selection"
      >
        {options.map((option, index) => {
          const isSelected = selectedOption === option.id;
          const resultSurfaceClass = getPickResultSurfaceClass(isSelected, isCorrect);

          // The button is the grid item directly here (no wrapper div),
          // so the orphan classes go on the button and replace `w-full`.
          const isSingleColumn = options.length > 4;
          const isOrphan = isOrphanCard({ index, total: options.length, isSingleColumn });
          const orphanLayout = isOrphan ? ORPHAN_CARD_CLASSES : 'w-full';

          const getBackgroundClass = () => {
            if (resultSurfaceClass) return '';
            if (locked) return getPickLockedBackgroundClass(isSelected);
            if (isSelected) return 'bg-white';
            return 'bg-surface-muted';
          };

          const getBorderClass = () => {
            if (resultSurfaceClass) return resultSurfaceClass;
            if (locked) return getPickLockedBorderClass(isSelected);
            if (isSelected) return 'border border-white';
            return 'border border-border';
          };

          const getHoverClass = () => {
            if (disabled || hasResult || locked) return '';
            return 'hover:border-muted-foreground';
          };

          const labelClass =
            locked && isSelected
              ? getPickLockedLabelClass()
              : getPickOptionTextClass(isSelected, hasResult);
          const showLockBadge = locked && isSelected && !hasResult;

          return (
            <button
              type="button"
              key={option.id}
              onClick={() => {
                if (!disabled && !locked) onOptionSelect(isSelected ? undefined : option.id);
              }}
              disabled={disabled || locked}
              data-testid={`option-${option.id}`}
              data-testresult={
                isSelected && hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined
              }
              data-selected={isSelected ? 'true' : 'false'}
              data-locked={locked ? 'true' : undefined}
              className={`
                group peer h-auto min-h-[140px] p-6 rounded-xl transition-smooth flex flex-col items-center justify-center relative
                ${orphanLayout}
                ${getBackgroundClass()}
                ${getBorderClass()}
                ${getHoverClass()}
                ${disabled || locked ? 'cursor-not-allowed' : ''}
                ${disabled && !hasResult && !locked ? 'opacity-90' : ''}
              `}
            >
              {option.popularityPct !== undefined && options.length >= 2 && (
                <div className="absolute top-2 left-2">
                  <OptionPercentageStat
                    percentage={option.popularityPct}
                    isSelected={isSelected && !hasResult && !locked}
                  />
                </div>
              )}

              {option.thirdImageUrl && (
                <div className="absolute top-2 right-2 w-10 h-10">
                  <img
                    src={option.thirdImageUrl}
                    alt="Secondary"
                    className="w-full h-full object-contain"
                  />
                </div>
              )}

              <div className="relative flex flex-col items-center space-y-3">
                {option.imageUrl && (
                  <img
                    src={option.imageUrl}
                    alt={option.label}
                    className="w-16 h-16 object-contain"
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <div
                  className={`${
                    option.label.split(' ').some(l => l.length > 14) ? 'text-sm' : 'text-sm'
                  } font-sans font-semibold capitalize text-center min-h-12 ${labelClass}`}
                >
                  {option.label}
                </div>
                {showLockBadge && (
                  <LockBadge
                    data-testid={`lock-badge-${option.id}`}
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2"
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </QuestionCard>
  );
};

export default CustomPick;
